<template>
    <div class="card card-with-shadow border-0 mb-4">
        <div class="card-header bg-transparent px-4 py-3">
            <h4 class="m-0 p-0">{{ $t('Payments') }}</h4>
        </div>

        <div class="card-body">
            <app-overlay-loader v-if="countsLoading" />
            <div v-else>
                <div class="row">
                    <!-- Cash on hand + Unverified online -->
                    <div v-for="card in paymentCards" :key="card.type" class="col-12 col-md-4 mb-3">
                        <div class="pay-card" :class="{ active: activeType === card.type }"
                            :style="{ borderLeftColor: colorHex(card.color) }" @click="selectCard(card)">
                            <div class="amount" :style="{ color: colorHex(card.color) }">{{ money(counts[card.type]) }}</div>
                            <div class="title">{{ $t(card.title) }}</div>
                        </div>
                    </div>

                    <!-- Pending payments parent -->
                    <div class="col-12 col-md-4 mb-3">
                        <div class="pay-card" :class="{ active: activeType === 'pending' }"
                            :style="{ borderLeftColor: colorHex('orange') }" @click="selectCard({ type: 'pending', title: 'Pending Payments' })">
                            <div class="amount" :style="{ color: colorHex('orange') }">{{ money(counts.pending) }}</div>
                            <div class="title">{{ $t('Pending Payments') }}</div>
                        </div>
                    </div>
                </div>

                <!-- Pending payments breakdown -->
                <div class="row">
                    <div class="col-12 mb-1"><small class="text-muted">{{ $t('Pending Payments breakdown') }}</small></div>
                    <div v-for="sub in pendingSub" :key="sub.type" class="col-12 col-md-4 mb-3">
                        <div class="pay-card sub" :class="{ active: activeType === sub.type }"
                            @click="selectCard(sub)">
                            <div class="amount">{{ money(counts[sub.type]) }}</div>
                            <div class="title">{{ $t(sub.title) }}</div>
                        </div>
                    </div>
                </div>

                <!-- expandable list -->
                <div v-if="activeType" class="mt-2 border-top pt-3">
                    <h6 class="mb-2">{{ $t(activeTitle) }}</h6>
                    <app-overlay-loader v-if="listLoading" />
                    <div v-else class="table-responsive">
                        <table class="table table-hover table-sm mb-0">
                            <thead>
                                <tr v-if="activeGroup === 'payment'">
                                    <th>{{ $t('Order ID') }}</th>
                                    <th>{{ $t('Order Date') }}</th>
                                    <th>{{ $t('Patient Name') }}</th>
                                    <th>{{ $t('Branch') }}</th>
                                    <th class="text-right">{{ $t('Amount') }}</th>
                                    <th>{{ $t('Mode') }}</th>
                                    <th>{{ $t('Date of receipt') }}</th>
                                    <th>{{ $t('Practitioner') }}</th>
                                    <th>{{ $t('Verified') }}</th>
                                </tr>
                                <tr v-else>
                                    <th>{{ $t('Order ID') }}</th>
                                    <th>{{ $t('Order Date') }}</th>
                                    <th>{{ $t('Patient Name') }}</th>
                                    <th>{{ $t('Branch') }}</th>
                                    <th>{{ $t('Practitioner') }}</th>
                                    <th>{{ $t('Order Status') }}</th>
                                    <th>{{ $t('Payment Status') }}</th>
                                    <th class="text-right">{{ $t('Due Amount') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="!listRows.length"><td :colspan="activeGroup === 'payment' ? 9 : 8" class="text-center text-muted">{{ $t('No records.') }}</td></tr>
                                <template v-else>
                                    <tr v-for="row in listRows" :key="row.id + '-' + (row.receipt_date || '')">
                                        <td><a :href="orderHref(row.id)" target="_blank" rel="noopener" class="text-primary">{{ row.order_ref }}</a></td>
                                        <td>{{ row.order_date }}</td>
                                        <td>{{ row.patient }}</td>
                                        <td>{{ row.branch }}</td>
                                        <template v-if="activeGroup === 'payment'">
                                            <td class="text-right">{{ money(row.amount) }}</td>
                                            <td>{{ row.mode }}</td>
                                            <td>{{ row.receipt_date }}</td>
                                            <td>{{ row.practitioner }}</td>
                                            <td>{{ row.verified }}</td>
                                        </template>
                                        <template v-else>
                                            <td>{{ row.practitioner }}</td>
                                            <td><span v-if="row.order_status.name" :class="`badge badge-${row.order_status.class} badge-pill`">{{ $t(row.order_status.name) }}</span></td>
                                            <td><span v-if="row.payment_status.name" :class="`badge badge-${row.payment_status.class} badge-pill`">{{ $t(row.payment_status.name) }}</span></td>
                                            <td class="text-right">{{ money(row.due_amount) }}</td>
                                        </template>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { axiosGet, urlGenerator } from "../../../../common/Helper/AxiosHelper";
import { numberWithCurrencySymbol } from "../../../Helper/Helper";
import { PAYMENT_DASHBOARD_COUNTS, PAYMENT_DASHBOARD_LIST } from "../../../Config/ApiUrl-CP";

export default {
    name: "PaymentsDashboardSection",
    data() {
        return {
            countsLoading: false,
            listLoading: false,
            counts: {},
            activeType: null,
            activeTitle: '',
            activeGroup: 'payment',
            listRows: [],
            paymentCards: [
                { type: 'cash', title: 'Cash on Hand', color: 'green' },
                { type: 'online', title: 'Unverified Online Payments', color: 'blue' },
            ],
            pendingSub: [
                { type: 'pending_dispatched', title: 'Orders Dispatched' },
                { type: 'pending_ready', title: 'Orders Ready' },
                { type: 'pending_not_ready', title: 'Orders Not Ready' },
            ],
        };
    },
    computed: {
        ...mapGetters(['getBranchOrWarehouseId']),
    },
    methods: {
        money(v) {
            return numberWithCurrencySymbol(v != null ? v : 0);
        },
        colorHex(color) {
            return { green: '#00a65a', blue: '#3874ff', orange: '#fd7e14' }[color] || '#3874ff';
        },
        branchParam() {
            return this.getBranchOrWarehouseId ? `branch_or_warehouse_id=${this.getBranchOrWarehouseId}` : '';
        },
        fetchCounts() {
            this.countsLoading = true;
            axiosGet(`${PAYMENT_DASHBOARD_COUNTS}?${this.branchParam()}`).then(res => {
                this.counts = res.data || {};
            }).finally(() => { this.countsLoading = false; });
        },
        selectCard(card) {
            if (this.activeType === card.type) {
                this.activeType = null;
                this.listRows = [];
                return;
            }
            this.activeType = card.type;
            this.activeTitle = card.title;
            this.fetchList(card.type);
        },
        fetchList(type) {
            this.listLoading = true;
            this.listRows = [];
            axiosGet(`${PAYMENT_DASHBOARD_LIST}?type=${type}&${this.branchParam()}`).then(res => {
                this.activeGroup = (res.data && res.data.group) ? res.data.group : 'payment';
                this.listRows = (res.data && res.data.rows) ? res.data.rows : [];
            }).finally(() => { this.listLoading = false; });
        },
        orderHref(id) {
            return urlGenerator(`order/details/${id}`);
        },
    },
    mounted() {
        this.fetchCounts();
    },
    watch: {
        getBranchOrWarehouseId() {
            this.activeType = null;
            this.listRows = [];
            this.fetchCounts();
        },
    },
};
</script>

<style scoped>
.pay-card {
    border: 1px solid #eef0f3;
    border-left: 4px solid #3874ff;
    border-radius: 6px;
    padding: 14px 16px;
    height: 100%;
    cursor: pointer;
    transition: box-shadow .15s;
}
.pay-card.sub { border-left-color: #c5ccd3; background: #fafbfc; }
.pay-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,.08); }
.pay-card.active { box-shadow: 0 0 0 2px rgba(56,116,255,.35); }
.pay-card .amount { font-size: 22px; font-weight: 700; line-height: 1.1; }
.pay-card .title { font-size: 13px; color: #5b6b79; margin-top: 2px; }
</style>
